import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "@/i18n/useTranslation";
import { TaskRole } from "@/components/scheduler/schedulerTypes";
import { supabase } from "@/integrations/supabase/client";
import { useAssignments } from "@/components/assignments/useAssignments";
import { FilterableCombobox } from "@/components/ui/filterable-combobox";
import { Task, Sprint } from './boardTypes';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

interface TaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (title: string, description: string, role: TaskRole, sprintId: string, employeeId?: number) => void;
  onDelete?: (taskId: string) => void;
  task?: Task | null; // Optional for create mode, required for edit mode
  boardId?: string | null;
  selectedSprintId?: string | null;
  selectedRole?: TaskRole | 'all';
  mode: 'create' | 'edit';
}

const roleDisplayNames: Record<TaskRole, string> = {
  'Admin': 'Admin',
  'Dev': 'Dev',
  '2D': '2D',
  '3D': '3D',
  'UI': 'UI',
  'Design': 'Design',
  'Audio': 'Audio',
  'Tech Art': 'Tech Art',
  'QA': 'QA'
};

export function TaskDialog({ 
  isOpen, 
  onClose, 
  onSubmit, 
  onDelete,
  task,
  boardId, 
  selectedSprintId,
  selectedRole, 
  mode 
}: TaskDialogProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [role, setRole] = useState<TaskRole>('Admin');
  const [sprintId, setSprintId] = useState<string>('');
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(false);
  const [backlogSprint, setBacklogSprint] = useState<Sprint | null>(null);
  const [sprintsLoaded, setSprintsLoaded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Employee selection state
  const [selectedEmployee, setSelectedEmployee] = useState<number | undefined>(undefined);
  
  // Get employees data
  const { employees, isLoading: isLoadingEmployees } = useAssignments();

  // Initialize form based on mode
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && task) {
        // Edit mode - populate with task data
        console.log(`Initializing edit form with task:`, task);
        setTitle(task.content || '');
        setDescription(task.description || '');
        setRole(task.role);
        setSelectedEmployee(task.employee_id || undefined);
        
        // Set sprint ID immediately when task data is available
        if (task.sprint_id) {
          console.log("Setting initial sprintId from task:", task.sprint_id);
          setSprintId(task.sprint_id);
        }
      } else {
        // Create mode - use defaults or selections from sidebar
        if (selectedRole && selectedRole !== 'all') {
          setRole(selectedRole);
        } else {
          // Default to Dev if 'all' is selected
          setRole('Dev');
        }
        
        // Reset fields in create mode
        setTitle('');
        setDescription('');
        setSelectedEmployee(undefined);
      }
    }
  }, [isOpen, task, selectedRole, mode]);

  // Fetch sprints when the dialog opens
  useEffect(() => {
    if (isOpen && boardId) {
      console.log(`Fetching sprints for boardId: ${boardId}`);
      fetchSprints();
    } else if (!isOpen) {
      // Reset state when dialog closes
      setSprintsLoaded(false);
    }
  }, [isOpen, boardId]);

  const fetchSprints = async () => {
    if (!boardId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('sprints')
        .select('*')
        .eq('board_id', boardId)
        .order('start_date', { ascending: false });
      
      if (error) throw error;
      
      if (data) {
        console.log("Fetched sprints:", data);
        
        // Find backlog sprint and set it separately
        const backlogSprint = data.find(sprint => sprint.status === 'backlog') || null;
        setBacklogSprint(backlogSprint);
        
        // Set regular sprints
        const regularSprints = data.filter(sprint => sprint.status !== 'backlog');
        setSprints(regularSprints);
        
        if (mode === 'edit') {
          // For edit mode, prioritize task's sprint ID
          const taskSprintExists = task && task.sprint_id && data.some(sprint => sprint.id === task.sprint_id);
          
          if (taskSprintExists) {
            console.log("Confirming sprintId from task is valid:", task?.sprint_id);
            setSprintId(task!.sprint_id);
          } else if (!sprintId && backlogSprint) {
            console.log("No valid sprint found, defaulting to backlog:", backlogSprint.id);
            setSprintId(backlogSprint.id);
          } else if (!sprintId && regularSprints.length > 0) {
            console.log("No backlog, defaulting to first sprint:", regularSprints[0].id);
            setSprintId(regularSprints[0].id);
          }
        } else {
          // For create mode
          // If selectedSprintId is provided, prioritize it as the default
          if (selectedSprintId && selectedSprintId !== 'all' && data.some(sprint => sprint.id === selectedSprintId)) {
            setSprintId(selectedSprintId);
          } else {
            // Set default sprint to the current one (in progress)
            const currentSprint = regularSprints.find(sprint => sprint.status === 'in_progress');
            if (currentSprint) {
              setSprintId(currentSprint.id);
            } else if (backlogSprint) {
              // If no current sprint, use backlog
              setSprintId(backlogSprint.id);
            } else if (regularSprints.length > 0) {
              // Otherwise use first available sprint
              setSprintId(regularSprints[0].id);
            }
          }
        }
        
        setSprintsLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching sprints:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    
    // Keep the role and sprint, but reset selected employee
    setSelectedEmployee(undefined);
  };

  const handleSubmit = (e: React.FormEvent, shouldClose: boolean = true) => {
    e.preventDefault();
    if (!title.trim() || !sprintId) return;
    
    onSubmit(title, description, role, sprintId, selectedEmployee);
    
    if (shouldClose) {
      if (mode === 'create') {
        resetForm();
      }
      onClose();
    } else {
      // Only for create & continue
      resetForm();
      setTimeout(() => {
        const titleInput = document.getElementById('title');
        if (titleInput) titleInput.focus();
      }, 10);
    }
  };

  const handleDelete = () => {
    if (task && onDelete) {
      onDelete(task.id);
      onClose();
      toast.success(t('board.taskDeleted'));
    }
    setDeleteDialogOpen(false);
  };

  // Filter employees based on the selected role
  const filterEmployeesByRole = (emps: typeof employees) => {
    return emps?.filter(emp => emp.role === role) || [];
  };

  // Dialog title based on mode
  const dialogTitle = mode === 'create' ? t('board.createTask') : t('board.editTask');
  const submitButtonText = mode === 'create' ? t('common.create') : t('common.update');

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[700px] flex gap-4">
          <div className="flex-1">
            <DialogHeader>
              <DialogTitle>{dialogTitle}</DialogTitle>
              <DialogDescription>
                {t('board.taskDescription')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={(e) => handleSubmit(e, true)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="title" className="text-sm font-medium">
                  {t('board.taskTitle')}
                </label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('board.taskTitle')}
                  required
                  autoFocus
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium">
                  {t('board.taskDescription')}
                </label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('board.taskDescription')}
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="role" className="text-sm font-medium">
                  {t('employees.role')}
                </label>
                <Select 
                  value={role} 
                  onValueChange={(value) => {
                    setRole(value as TaskRole);
                    // Reset employee selection when role changes to avoid having an employee with a different role
                    setSelectedEmployee(undefined);
                  }}
                >
                  <SelectTrigger id="role">
                    <SelectValue placeholder={t('board.selectRole')} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleDisplayNames).map(([role, displayName]) => (
                      <SelectItem key={role} value={role}>{displayName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Employee selection */}
              <FilterableCombobox
                items={employees || []}
                value={selectedEmployee}
                onValueChange={setSelectedEmployee}
                filterFunction={filterEmployeesByRole}
                showFilterToggle={true}
                placeholder={t('employees.selectEmployee')}
                searchPlaceholder={t('employees.searchEmployee')}
                noResultsMessage={t('employees.noEmployeesFound')}
                getItemValue={(employee) => employee.id}
                getItemLabel={(employee) => employee.name}
                getItemSecondaryLabel={(employee) => employee.role}
                label={t('employees.assignTo')}
                disabled={isLoadingEmployees}
              />
              
              <div className="space-y-2">
                <label htmlFor="sprint" className="text-sm font-medium required">
                  {t('board.sprint')} *
                </label>
                <Select 
                  key={`sprint-select-${sprintsLoaded}-${sprintId}`}
                  value={sprintId} 
                  onValueChange={(value) => setSprintId(value)}
                  required
                >
                  <SelectTrigger id="sprint" disabled={loading}>
                    <SelectValue placeholder={loading ? t('common.loading') : t('board.selectSprint')} />
                  </SelectTrigger>
                  <SelectContent>
                    {backlogSprint && (
                      <SelectItem key={backlogSprint.id} value={backlogSprint.id}>
                        {backlogSprint.title} ({t('board.backlog')})
                      </SelectItem>
                    )}
                    
                    {sprints.map((sprint) => (
                      <SelectItem key={sprint.id} value={sprint.id}>
                        {sprint.title} 
                        {sprint.status === 'in_progress' ? ` (${t('board.current')})` : 
                        sprint.status === 'planned' ? ` (${t('board.planned')})` : 
                        ` (${t('board.completed')})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <DialogFooter className="gap-2 flex-wrap">
                <Button type="button" variant="outline" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
                
                {mode === 'create' && (
                  <Button 
                    type="button" 
                    onClick={(e) => handleSubmit(e, false)}
                    variant="secondary"
                    disabled={!title.trim() || !sprintId}
                  >
                    {t('common.create')} & {t('common.continue')}
                  </Button>
                )}
                
                <Button type="submit" disabled={!title.trim() || !sprintId}>
                  {submitButtonText}
                </Button>
              </DialogFooter>
            </form>
          </div>
          
          {/* Sidebar for actions - only shown in edit mode */}
          {mode === 'edit' && task && (
            <div className="w-[200px] border-l pl-4 flex flex-col">
              <h3 className="text-sm font-medium mb-4">{t('common.actions')}</h3>
              
              <div className="">
                <Button 
                  onClick={() => setDeleteDialogOpen(true)} 
                  variant="outline" 
                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Confirmation dialog for delete action */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('board.deleteTaskConfirmation')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('board.deleteTaskWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
